import ListView from '@/components/datagrid/ListView';
import CallDetails from '@/components/forms/calls/CallDetails';
import useApiQuery from '@/hooks/useApiQuery';
import useTitle from '@/hooks/useTitle';
import formatDuration from '@/services/utils/formatDuration';
import useCrmStore from '@/store/crm';
import {
  CallMade,
  CallMissed,
  CallMissedOutgoing,
  CallReceived,
} from '@mui/icons-material';
import { Tooltip, Button, Menu, useTheme } from '@mui/material';
import { useEffect, useState } from 'react';
import { Box } from '@mui/system';
import { DateRange } from 'react-date-range';
import { de } from 'date-fns/locale';
import { KeyboardArrowDown } from '@mui/icons-material';
import useSearchStore from '@/store/search';

export default function CallsPage() {
  useTitle('Anrufe');
  const theme = useTheme();

  const [filterModel] = useState(null);
  const [sortModel] = useState(null);

  const [searchQuery, setSearchQuery] = useState('');
  const { registerLocalSearch, unregisterLocalSearch } = useSearchStore();

  useEffect(() => {
    registerLocalSearch({
      key: 'calls',
      label: 'Anrufe',
      onSearch: (query) => setSearchQuery(query),
      onClear: () => setSearchQuery(''),
    });

    return () => {
      unregisterLocalSearch();
    };
  }, [registerLocalSearch, unregisterLocalSearch]);

  const { range, setRange, filter, setFilter, sort, group } = useCrmStore();
  const [tempRange, setTempRange] = useState(range);
  const [timeframeSelector, setTimeframeSelector] = useState(null);

  const startOfDay = new Date(range.startDate);
  startOfDay.setHours(0, 0, 0, 0);
  const endDate = new Date(range.endDate);
  const mongoFilter = {
    $and: [{ timeStart: { $gt: startOfDay } }, { timeStart: { $lt: endDate } }],
    type: 'call',
  };

  if (sort) {
    mongoFilter.sort = sort;
  }

  if (filter) {
    mongoFilter.$and.push(filter);
  }

  if (group) {
    mongoFilter.$and.push({ ringingGroups: group.accountId });
  }

  if (searchQuery) {
    mongoFilter.$or = [
      { callerName: { $regex: searchQuery, $options: 'i' } },
      { callerNumber: { $regex: searchQuery, $options: 'i' } },
      { 'callFlow.0.calledName': { $regex: searchQuery, $options: 'i' } },
      { 'callFlow.0.calledNumber': { $regex: searchQuery, $options: 'i' } },
    ];
  }

  const {
    // eslint-disable-next-line no-unused-vars
    isPending,
    data: callbacks,
    refetch,
  } = useApiQuery('call', mongoFilter);

  const columns = [
    {
      field: 'outgoing',
      headerName: 'Richtung',
      renderCell: (params) => (params.value ? 'Ausgehend' : 'Eingehend'),
    },
    {
      field: 'pickup',
      headerName: 'Angenommen',
      renderCell: (params) => (params.value ? 'Ja' : 'Nein'),
    },
    {
      field: 'type',
      headerName: 'Art',
      width: 50,
      renderCell: (params) =>
        (params.row.outgoing && params.row.callFlow?.[0]?.connected && (
          <Tooltip title="Ausgehend">
            <CallMade sx={{ fontSize: '16px' }} />
          </Tooltip>
        )) ||
        (params.row.outgoing && !params.row.callFlow?.[0]?.connected && (
          <Tooltip title="Ausgehend (keine Verbindung)">
            <CallMissedOutgoing sx={{ fontSize: '16px' }} />
          </Tooltip>
        )) ||
        (!params.row.outgoing && params.row.pickup && (
          <Tooltip title="Eingehend">
            <CallReceived sx={{ fontSize: '16px' }} />
          </Tooltip>
        )) || (
          <Tooltip title="Verpasst">
            <CallMissed sx={{ fontSize: '16px' }} />
          </Tooltip>
        ),
      disableColumnMenu: false,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'timeStart',
      headerName: 'Zeitpunkt',
      width: 150,
      renderCell: (params) => new Date(params.row.timeStart).toLocaleString(),
    },
    {
      field: 'callerName',
      headerName: 'Anrufer:in / Kunde',
      flex: 0.5,
      renderCell: (params) =>
        (params.row.callerName && (
          <Tooltip title={params.row.callerNumber}>
            {params.row.callerName}
          </Tooltip>
        )) ||
        params.row.callerNumber,
      valueGetter: (value, row) => {
        return row.callerName || row.callerNumber;
      },
    },
    {
      field: 'calledNumber',
      headerName: 'Empfänger:in',
      flex: 0.5,
      renderCell: (params) =>
        (params.row.callFlow?.[0]?.calledName && (
          <Tooltip title={params.row.callFlow?.[0]?.calledNumber}>
            {params.row.callFlow?.[0]?.calledName}
          </Tooltip>
        )) ||
        params.row.callFlow?.[0]?.calledNumber,
      valueGetter: (value, row) =>
        row.callFlow?.[0]?.calledName || row.callFlow?.[0]?.calledNumber,
    },
    {
      field: 'pickupName',
      headerName: 'Angenommen von',
      flex: 0.5,
      renderCell: (params) =>
        params.row.pickup &&
        `${params.row.pickupFirstName} ${params.row.pickupLastName}`,
      valueGetter: (value, row) =>
        row.pickup && `${row.pickupFirstName} ${row.pickupLastName}`,
    },
    {
      field: 'duration',
      headerName: 'Dauer',
      width: 100,
      renderCell: (params) => {
        if (!params.row.outgoing) {
          return params.row.pickup && formatDuration(params.value / 1000);
        } else {
          return formatDuration(params.value / 1000);
        }
      },
      valueGetter: (value, row) => {
        if (!row.outgoing) {
          return row.pickup
            ? new Date(row.timeEnd) - new Date(row.pickupTime)
            : 0;
        } else {
          return new Date(row.timeEnd) - new Date(row.timeStart);
        }
      },
    },
    {
      field: 'waitingTime',
      headerName: 'Wartezeit',
      width: 100,
      valueGetter: (value, row) => {
        return (
          !row.outgoing &&
          Math.floor(
            (new Date(row.pickupTime || row.timeEnd) -
              new Date(row.timeStart)) /
              1000,
          )
        );
      },
      renderCell: (params) => {
        return params.value && `${params.value} s`;
      },
    },
  ];

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
      }}
    >
      <ListView
        key={JSON.stringify(mongoFilter)}
        rows={callbacks || []}
        columns={columns}
        detailComponents={[{ Component: CallDetails, title: 'Test' }]}
        actions={[
          <>
            <Button
              variant="contained"
              disableElevation
              onClick={(evt) => setTimeframeSelector(evt.target)}
              endIcon={<KeyboardArrowDown />}
              ref={timeframeSelector}
              sx={{ width: '220px' }}
            >
              {range.startDate.toLocaleDateString('de-DE', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
              })}{' '}
              -{' '}
              {range.endDate.toLocaleDateString('de-DE', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
              })}
            </Button>
            <Menu
              key={'timeframe'}
              anchorEl={timeframeSelector}
              open={!!timeframeSelector}
              onClose={() => setTimeframeSelector(null)}
              onClick={(evt) => evt.stopPropagation()}
              sx={{ minWidth: '200px', minHeight: '200px' }}
            >
              <DateRange
                locale={de}
                ranges={[tempRange]}
                onChange={(item) => {
                  const selection = item.selection;
                  setTempRange({
                    ...selection,
                    key: 'selection',
                  });
                }}
                maxDate={new Date()}
                showDateDisplay={false}
                rangeColors={[theme.palette.primary.main]}
              />
              <Box sx={{ textAlign: 'center', mt: 1 }}>
                <Button
                  variant="contained"
                  sx={{ mx: 2, width: '90%' }}
                  onClick={() => {
                    const newStart = new Date(tempRange.startDate);
                    newStart.setHours(0, 0, 0, 0);
                    const newEnd = new Date(tempRange.endDate);
                    newEnd.setHours(23, 59, 59, 999);
                    setRange({
                      startDate: newStart,
                      endDate: newEnd,
                      key: 'selection',
                    });
                    setTimeframeSelector(null);
                  }}
                >
                  Anwenden
                </Button>
              </Box>
            </Menu>
          </>,
          <>
            <Button
              key="filterButton"
              variant="outlined"
              onClick={() => setFilter(null)}
              disabled={!filter}
            >
              Filter Löschen
            </Button>
          </>,
        ]}
        sortModel={sortModel}
        filterModel={filterModel}
        refetch={refetch}
        columnVisibilityModel={{ outgoing: false, pickup: false }}
      />
    </Box>
  );
}
